/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState, useMemo, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { debounceTime } from 'rxjs/operators';

import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import { EnvironmentContext, ForgeUIRenderer } from '@atlassian/forge-ui/ui';
import type { Extension, ViewportDimensions } from '@atlassian/forge-ui/ui';
import type { ChangeListener } from '@atlassian/forge-ui/custom-ui/iframe/types';
import { dequal } from 'dequal/lite';
import { emit, on } from '@atlassian/forge-ui/events';
import type {
	ProductEnvironment,
	CoreData,
	ExtensionData,
	ExtensionViewData,
	ForgeDoc,
	ForgeUIExtensionType,
	ExtensionPayload,
} from '@atlassian/forge-ui-types';
import { isCustomUI } from '@atlassian/forge-ui';
import { makeADFRenderer } from '@atlassian/forge-ui-kit-adf-renderer';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';

import Blanket from '@atlaskit/blanket';
import Portal from '@atlaskit/portal';
import { layers } from '@atlaskit/theme/constants';
import Spinner from '@atlaskit/spinner';

import { type FlagFunctions, getFlagProvider } from '../../utils/getFlagProvider';
import { type CustomEditContext, type EditorAPI } from '../../types';

import { getCustomBridgeMethods } from './getCustomBridgeMethods';
import { getCustomBridgeMethodsV2 } from './getCustomBridgeMethodsV2';
import { calculateViewportSizeHeight, type ViewportSizeType } from '@atlassian/forge-ui/ui';
import { fg } from '@atlaskit/platform-feature-flags';
import { useReemitReadyEvent } from '../hooks/useReemitReadyEvent';
import { ChromelessEditor, CommentEditor } from '@atlassian/forge-ui-kit-editor';
import { useMacroStaticContentView } from '../hooks/useMacroStaticContentView';
import type { DocNode } from '@atlaskit/adf-schema';
import { extractExtensionStaticViewProperties } from '../../utils/extractExtensionStaticViewProperties';
import { ReactRenderer } from '@atlaskit/renderer';

const spinnerContainerStyles = css({
	display: 'flex',
	width: '100%',
	height: '100%',
	alignItems: 'center',
	justifyContent: 'center',
	pointerEvents: 'none',
});

// Module-level constant to ensure referential stability across renders.
// Emits FORGE_CORE_MACRO_CONFIG_CHANGED when the macro config changes, scoped
// to the specific extension via getContext().emit (pre-bound to extensionDetails).
export const macroConfigChangeListener: ChangeListener = (prev, next, changes, getContext) => {
	if (!changes.extensionPayloadChanges.config) {
		return;
	}
	const prevConfig = prev.extensionPayload?.config;
	const nextConfig = next.extensionPayload?.config;
	// extensionPayloadChanges is a reference pre-filter only — verify actual value change
	// to avoid spurious emits when Confluence reconstructs the payload object.
	if (!dequal(prevConfig, nextConfig)) {
		getContext().emit('FORGE_CORE_MACRO_CONFIG_CHANGED', { config: nextConfig });
	}
};

// This is added to contextChangeListeners of the ForgeUIRenderer, and this setup is currently only applicable to
// v2 bridge runtime (https://hello.atlassian.net/wiki/x/ep08mwE), and will not have impact to current behaviors.
// All the changes are guarded behind `forge-ui-bridge-core-reset` feature gate, and hence no impact on the production
// environment for this change.
const MACRO_CONFIG_CHANGE_LISTENERS: ReadonlyArray<ChangeListener> = [macroConfigChangeListener];
export interface ForgeUIExtensionProps {
	accountId: string;
	contextIds: string[];
	coreData: CoreData;
	customEditContext?: CustomEditContext;
	dataProviders?: ProviderFactory;
	editorAPI?: EditorAPI;
	environment: ProductEnvironment;
	extension: Extension;
	extensionData: ExtensionData;
	extensionPayload: ExtensionPayload;
	extensionViewData: ExtensionViewData;
	flags: FlagFunctions;
	forgeMacroExtensions?: readonly ForgeUIExtensionType[] | null;
	getContextToken?: () => Promise<string>;
	isBodiedMacro?: boolean;
	isEditing: boolean;
	locale: string;
	onForgeDocUpdated?: (forgeDoc?: ForgeDoc) => void;
	productExtensionHandlers?: ExtensionHandlers;
	useRouteActions?: () => {
		[key: string]: (...args: any[]) => any;
		push: (url: string, forceReload?: boolean) => void;
	};
	viewportDimensions?: ViewportDimensions;
}

export const ForgeUIExtension = ({
	accountId,
	contextIds,
	coreData,
	dataProviders,
	extensionData,
	extensionViewData,
	extensionPayload,
	environment,
	extension,
	forgeMacroExtensions,
	isBodiedMacro,
	locale,
	flags,
	getContextToken,
	productExtensionHandlers,
	customEditContext,
	isEditing,
	editorAPI,
	onForgeDocUpdated,
	viewportDimensions,
	useRouteActions,
}: ForgeUIExtensionProps): JSX.Element => {
	const { cloudId, localId } = coreData;
	const modalExtension = customEditContext?.modalExtension;
	const bridge = customEditContext?.bridge;
	const memoizedCustomBridgeMethods = useMemo(() => {
		return getCustomBridgeMethods({ isEditing, localId, editorAPI });
	}, [isEditing, localId, editorAPI]);

	const memoizedCustomBridgeMethodsV2 = useMemo(() => {
		if (!fg('forge-ui-bridge-core-reset')) {
			return;
		}
		return getCustomBridgeMethodsV2({ editorAPI });
	}, [editorAPI]);
	const confluenceSpa = useRouteActions?.();

	const [macroConfigForgeDocSubject$] = useState(() => new ReplaySubject(10));
	const configValueRef = useRef(undefined);
	// Stores the forgeDoc for the macro config schema. The ref is often a render behind
	// the actual schema, so should only be used on the initial load.
	const macroConfigForgeDocRef = useRef<ForgeDoc | undefined>(undefined);

	// This sets up an event listener to respond to Editor's requests for the macro config schema.
	// When the GET_CONFIG_FORGE_DOC_${id} event is emitted, the payload includes the current
	// config value, and the resolve and reject functions of a Promise. The listener should be
	// resolving the promise with the value of the macro config forgeDoc.
	const id = `${extension.id}-${coreData.localId}`;
	const reconcile = useCallback(
		({ forgeDoc }: { forgeDoc: ForgeDoc }) => {
			Object.freeze(forgeDoc);
			macroConfigForgeDocRef.current = forgeDoc;
			macroConfigForgeDocSubject$.next(forgeDoc);
			emit(`CONFIG_FORGE_DOC_UPDATED_${id}`);
		},
		[id, macroConfigForgeDocSubject$],
	);

	useEffect(() => {
		const macroConfigForgeDocSubscription = on(
			`GET_CONFIG_FORGE_DOC_${id}`,
			async ({
				resolve,
				config,
			}: {
				config: undefined;
				reject: (reason?: unknown) => void;
				resolve: (value: unknown) => void;
			}) => {
				const subscription = macroConfigForgeDocSubject$.pipe(debounceTime(200)).subscribe({
					next: (forgeDoc) => {
						resolve({
							type: 'Root',
							props: {},
							children: [forgeDoc],
						});
						subscription.unsubscribe();
					},
				});
				configValueRef.current = config;
			},
		);

		return () => {
			macroConfigForgeDocSubscription.unsubscribe();
		};
	}, [id, macroConfigForgeDocSubject$]);

	/*the emitReadyEvent emited by the macro
	must be receive by cc-puppeteer an internal service responsible for
	generating PDFs from confluence pages, the issue is that it does not
	recieve events from other origins such as iframes, so we capture and
	reemit the event here */
	useReemitReadyEvent(fg('forge-ui-reemit-ready-event'));

	const { showFlag, closeFlag } = useMemo(() => getFlagProvider(flags), [flags]);

	const bridgeConfig = useMemo(
		() => ({
			...bridge,
			showFlag,
			closeFlag,
			reconcile,
			spaPush: confluenceSpa?.push,
		}),
		[bridge, showFlag, closeFlag, reconcile, confluenceSpa?.push],
	);

	// For bodied macros, adf content is stored in context.extension.macro.body. Forward the host
	// ProviderFactory (the nested renderer picks the task-decision + context-identifier providers off
	// it) so action items inside the macro body can persist their checked state. Gated to bodied
	// macros behind the feature flag; undefined otherwise so no providers are forwarded.
	const bodiedMacroDataProviders = useMemo(() => {
		if (!isBodiedMacro || !dataProviders || !fg('forge_ui_bodied_macro_task_persist')) {
			return undefined;
		}
		return dataProviders;
	}, [dataProviders, isBodiedMacro]);

	const AdfRendererWithEmbeddedMacros = useMemo(
		() =>
			makeADFRenderer({
				bridge: bridgeConfig,
				userId: accountId,
				customBridgeMethods: memoizedCustomBridgeMethods,
				customBridge: fg('forge-ui-bridge-core-reset') ? memoizedCustomBridgeMethodsV2 : undefined,
				getContextToken,
				extensionData,
				isEditing,
				productExtensionHandlers,
				cloudId,
				forgeMacroExtensions,
				productContextIds: contextIds,
				dataProviders: bodiedMacroDataProviders,
			}),
		[
			accountId,
			bridgeConfig,
			cloudId,
			contextIds,
			forgeMacroExtensions,
			getContextToken,
			extensionData,
			memoizedCustomBridgeMethods,
			memoizedCustomBridgeMethodsV2,
			isEditing,
			productExtensionHandlers,
			bodiedMacroDataProviders,
		],
	);

	let loadingComponent: ReactNode | undefined;

	if (!isCustomUI(extension) && modalExtension) {
		loadingComponent = (
			<Portal zIndex={layers.blanket()}>
				<Blanket isTinted={true} onBlanketClicked={() => modalExtension.closeModalExtension()}>
					<div css={spinnerContainerStyles}>
						<Spinner size="large" />
					</div>
				</Blanket>
			</Portal>
		);
	}

	// Sets the forgeDoc for classic sidebar macro config
	const handleMacroConfigForgeDoc = useCallback(
		(forgeDoc: ForgeDoc) => {
			if (forgeDoc.type === 'MacroConfig') {
				if (customEditContext) {
					throw new Error(
						'ForgeReconciler.addConfig() cannot be called from within a custom config resource',
					);
				}
				macroConfigForgeDocRef.current = forgeDoc;
				macroConfigForgeDocSubject$.next(forgeDoc);
				emit(`CONFIG_FORGE_DOC_UPDATED_${id}`);
			}
		},
		[customEditContext, id, macroConfigForgeDocRef, macroConfigForgeDocSubject$],
	);

	const { isContentViewAvailable, contentKey } = extractExtensionStaticViewProperties(
		extension,
		extensionPayload.config,
	);

	const { isLoading, content } = useMacroStaticContentView<DocNode>({
		extension,
		contentContainerId: extensionData?.content?.id,
		contentKey,
	});

	const shouldRenderStaticContentView =
		!modalExtension && isContentViewAvailable && (isLoading || content);
	if (shouldRenderStaticContentView) {
		return (
			<RendererActionsContext>
				{content ? <ReactRenderer document={content} /> : <Spinner size="small" />}
			</RendererActionsContext>
		);
	}

	return (
		<EnvironmentContext.Provider value={environment}>
			<ForgeUIRenderer
				accountId={accountId}
				bridge={bridgeConfig}
				cloudId={cloudId!}
				contextIds={[`ari:cloud:confluence::site/${cloudId}`]}
				components={{
					// Override to use latest renderer with embedded macro support for Confluence
					...(fg('platform-forge-ui-kit-adf-renderer')
						? {
								AdfRenderer: AdfRendererWithEmbeddedMacros,
							}
						: {}),
					ChromelessEditor,
					CommentEditor,
				}}
				extension={extension}
				extensionData={extensionData}
				extensionViewData={extensionViewData}
				extensionPayload={extensionPayload}
				customBridgeMethods={memoizedCustomBridgeMethods}
				customBridge={memoizedCustomBridgeMethodsV2}
				height={
					extension.properties.viewportSize
						? calculateViewportSizeHeight(extension.properties.viewportSize as ViewportSizeType)
						: undefined
				}
				locale={locale}
				localId={localId}
				getContextToken={getContextToken}
				product="confluence"
				onForgeDocUpdated={(forgeDoc: ForgeDoc) => {
					onForgeDocUpdated?.(forgeDoc);
					handleMacroConfigForgeDoc(forgeDoc);
				}}
				modalExtension={modalExtension}
				onModalExtensionClose={modalExtension?.closeModalExtension}
				loadingComponent={loadingComponent}
				viewportDimensions={viewportDimensions}
				contextChangeListeners={MACRO_CONFIG_CHANGE_LISTENERS}
			/>
		</EnvironmentContext.Provider>
	);
};
